import { ResourceData, useBaseCrudService } from 'framework/crud';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { TargetRule } from '../types';
import { usePositionService, PositionType } from 'domain/shared/employee';
import { cloneDeep } from 'lodash';
import { useTechnologyService, type Technology } from 'domain/shared/career';
import { LoadingParams } from 'framework/list';
import { calculateFulfillmentPercentage } from '../structureComparisonUtils';

export const CREATE_QUALIFICATION_STRUCTURES = 'PEOPLE_MANAGEMENT_QUALIFICATION_STRUCTURE_CREATE';
export const QUALIFICATION_STRUCTURE_LIST_READ = 'PEOPLE_MANAGEMENT_QUALIFICATION_STRUCTURE_LIST_READ';

const useQualificationStructureService = () => {
  const { addMessage } = useMessageService();
  const { sendPostRequest, sendGetRequest } = usePeopleManagementApi();

  const { getById, search: baseSearch } = useBaseCrudService({
    singleResourceUrl: '/qualifications/structures/:id',
    listResourceUrl: '/qualifications/structures',
    useService: usePeopleManagementApi,
  });

  const search = (request: ResourceData) => {
    const searchParams = cloneDeep(request);
    return baseSearch(searchParams);
  };

  const { findPositionById } = usePositionService();
  const { findById } = useTechnologyService();

  const getActiveQualificationStructure = async (loadParams: LoadingParams) => {
    try {
      const response = await sendGetRequest(`/qualifications/structures/active`);
      const data = await response.json();

      if (data && data.structure) {
        const enrichedStructurePromises = data.structure
          .filter((item: any) => {
            if (loadParams.filter?.position?.in) {
              return loadParams.filter.position.in.includes(item.positionId?.toString());
            }
            return true;
          })
          .map(async (item: any) => {
            const positionData = await findPositionById(item.positionId);
            const technologyData = await findById(item.technologyId);

            return {
              ...item,
              position: positionData.name || null,
              technology: technologyData.name || null,
              fulfillmentPercentage: calculateFulfillmentPercentage(item.capacity, item.actual),
            };
          });

        data.structure = await Promise.all(enrichedStructurePromises);
      }

      return Promise.resolve({ result: data.structure });
    } catch (error) {
      return Promise.reject(new Error('getActiveQualificationStructure: Failed to fetch active structure'));
    }
  };

  const submitQualificationStructure = async (
    totalHeadcount: number,
    rules: TargetRule[],
    positions: PositionType[],
    technologies: Technology[],
  ): Promise<boolean> => {
    try {
      const positionMap: Record<string, number> = {};
      positions.forEach(position => {
        positionMap[position.name] = position.id;
      });

      const technologyMap: Record<string, string> = {};
      technologies.forEach(technology => {
        technologyMap[technology.name] = technology.id;
      });
      const structure = [
        {
          capacity: totalHeadcount,
        },
        ...rules.map(rule => {
          const ruleObj: any = {
            capacity: rule.capacity || 0,
          };

          if (rule.competencyLevel) ruleObj.competencyLevel = rule.competencyLevel;
          if (rule.positionId) {
            ruleObj.positionId = rule.positionId;
          }

          if (rule.technologyId) {
            ruleObj.technologyId = rule.technologyId;
          }

          return ruleObj;
        }),
      ];

      const response = await sendPostRequest('/qualifications/structures', { structure });
      await response.json();

      addMessage(new SuccessMessage('Qualification structure submitted successfully'));
      return true;
    } catch (error) {
      addMessage(new ErrorMessage('Failed to submit qualification structure'));
      return false;
    }
  };

  const getGlobalPeopleManagersPermissions = async (): Promise<string[]> => {
    try {
      const response = await sendGetRequest(`/permissions`);
      return await response.json();
    } catch (error) {
      addMessage(new ErrorMessage('getGlobalPeopleManagersPermissions: Failed to fetch global permissions'));
      return [];
    }
  };

  const getQualificationStructureById = async (id: string) => {
    const response = await getById(id);
    return response;
  };

  return {
    getGlobalPeopleManagersPermissions,
    submitQualificationStructure,
    getActiveQualificationStructure,
    search,
    getById,
    getQualificationStructureById,
    baseSearch,
  };
};

export default useQualificationStructureService;
